import {
  useEffect,
  useState,
  MouseEvent as ReactMouseEvent,
  KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PRODUCT_PAGE } from "../routes";
import { lookupHs, HsLookupResponse, Country } from "../services/api";
import { PageTitle } from "./PageTitle";
import { ResultsSkeleton } from "./ResultsSkeleton";

// --- Location State Type ---
interface LocationState {
  desc: string;
  country: Country;
}

export const Results = () => {
  const location = useLocation();
  const state = location.state as LocationState | null;

  const navigate = useNavigate();
  const desc = state?.desc ?? "";
  const country = state?.country;

  const [products, setProducts] = useState<HsLookupResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const handleCopyHsCode = async (
    e: ReactMouseEvent | ReactKeyboardEvent,
    code: string,
    id: number
  ) => {
    e.stopPropagation();
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
      setCopiedId(id);
      setTimeout(() => setCopiedId((current) => (current === id ? null : current)), 1500);
    } catch (err) {
      console.error("Failed to copy HS code", err);
    }
  };

  useEffect(() => {
    if (!desc || !country?.id) return;

    (async () => {
      try {
        const data = await lookupHs(desc, country.id);
        setProducts(data);
      } catch (err) {
        console.error("HS lookup failed", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [desc, country?.id]);

  if (loading) return <ResultsSkeleton />;

  return (
    <div className="min-h-screen hero-bg flex items-start">
      <div className="w-[92vw] max-w-[1100px] mx-auto my-12">
        <PageTitle />

        <div className="inline-flex gap-2 bg-white px-4 py-3 rounded-full shadow">
          <span className="bg-neutral-100 px-3 py-1 rounded-full text-sm font-medium">
            {desc}
          </span>

          <span className="bg-neutral-100 px-3 py-1 rounded-full text-sm font-medium">
            from {country?.name}
            <button
              aria-label="remove picked"
              className="ml-2"
              onClick={() =>
                window.history.length > 1 ? navigate(-1) : navigate("/")
              }
            >
              ×
            </button>
          </span>
        </div>

        {/* Products list or empty state */}
        <div className="mt-7 bg-white rounded-2xl shadow-xl p-5">
          {products.length > 0 ? (
            <>
              <h2 className="text-lg font-semibold mb-3">
                What category best describes your product?
              </h2>

              <ul className="divide-y divide-neutral-200">
                {products.map((item) => (
                  <li key={item.id} className="py-3">
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        navigate(PRODUCT_PAGE, {
                          state: {
                            product: item,
                            query: { term: desc, country },
                          },
                        })
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          navigate(PRODUCT_PAGE, {
                            state: {
                              product: item,
                              query: { term: desc, country },
                            },
                          });
                        }
                      }}
                      className="w-full text-left relative flex items-start justify-between gap-3 px-1 py-2 rounded-md hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-blue-300 cursor-pointer"
                    >
                      <div className="pr-8">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-base">
                            {item.shortDescription || item.description}
                          </span>
                          <span className="text-sm bg-neutral-100 rounded-full px-2.5 py-0.5">
                            {item.hsCodeFormatted}
                          </span>
                          <button
                            type="button"
                            aria-label="Copy HS code"
                            title={copiedId === item.id ? "Copied!" : "Copy HS code"}
                            onClick={(e) =>
                              handleCopyHsCode(e, item.hsCodeFormatted, item.id)
                            }
                            className="text-neutral-400 hover:text-neutral-700 transition"
                          >
                            {copiedId === item.id ? (
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                              >
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            ) : (
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                              >
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                              </svg>
                            )}
                          </button>
                        </div>
                        <p className="mt-1 text-sm text-neutral-600">
                          {item.description}
                        </p>
                      </div>
                      <span
                        aria-hidden
                        className="text-2xl text-neutral-400 group-hover:bg-neutral-700 group-hover:text-white rounded-full w-7 h-7 flex items-center justify-center transition"
                      >
                        ›
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <div className="text-center py-8">
              <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                No products found
              </h3>
              <p className="text-neutral-600 mb-4">
                We couldn't find any products matching your description or code. Please
                check your input and try again.
              </p>
              <button
                onClick={() => navigate("/")}
                className="inline-flex items-center gap-2 bg-blue-600 text-white rounded-full px-5 py-2.5 font-medium hover:bg-blue-700 transition"
              >
                Try a different search
              </button>
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-start">
          <button
            className="text-blue-700 font-medium hover:underline whitespace-nowrap"
            onClick={() =>
              window.history.length > 1 ? navigate(-1) : navigate("/")
            }
          >
            ← Back
          </button>

          <p className="text-sm text-neutral-600 max-w-3xl">
            If no matching category is found, please return to the previous step and
            clarify the product description, for example by adding details such as
            materials, fabrics or product composition, product gender or size.
          </p>
        </div>
      </div>
    </div>
  );
};